import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { browserGet, browserModeKey, browserSessionHeader, browserSessionKey } from './toolBrowser/client';
import { ToolBrowserActivity, ToolBrowserInfrastructure } from './toolBrowser/Activity';
import { ToolBrowserGrants } from './toolBrowser/Grants';

declare global {
  interface Window { __mcToolBrowserToken?: string }
}

type BrowserApp = { id: string; name: string; slug: string; can_create_conversation: boolean };
type BrowserConversation = { id: string; app_id: string; title: string | null; status: string; updated_at: string; can_send: boolean };
type BrowserMessage = { id: string; role: string; content: string; status: string; created_at: string };
type BrowserSession = {
  subject: { id: string; name: string | null; email: string | null };
  capabilities: { apps_read: boolean; conversations_read: boolean; messages_write: boolean; servers_read?: boolean; executors_read?: boolean; runs_read?: boolean; logs_read?: boolean; grant_management?: boolean };
  project_access: boolean;
  session_id: string;
  csrf_token: string;
};

const sessionMismatchText = 'Browser session changed; open the tool-token login link again.';

async function browserSend(path: string, csrf: string, content: string): Promise<void> {
  const response = await fetch(path, {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: { 'Content-Type': 'application/json', 'X-Browser-CSRF': csrf, ...browserSessionHeader() },
    body: JSON.stringify({ content, auto_process: true })
  });
  if (!response.ok) throw new Error(response.status === 401 ? 'Browser session expired or revoked.' : 'Message could not be sent.');
}

async function browserCreateConversation(appId: string, csrf: string, title: string): Promise<string> {
  const response = await fetch(`/api/browser/apps/${encodeURIComponent(appId)}/conversations`, {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: { 'Content-Type': 'application/json', 'X-Browser-CSRF': csrf, ...browserSessionHeader() },
    body: JSON.stringify({ title })
  });
  if (!response.ok) throw new Error(response.status === 401 ? 'Browser session expired or revoked.' : 'Conversation could not be created.');
  const created = await response.json() as { id: string };
  if (!created.id) throw new Error('Conversation creation returned no identity.');
  return created.id;
}

function ToolBrowserLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [{ token, hasPriorAuth }] = useState(() => {
    // An attempted token switch must never reuse an older, stronger cookie in
    // this tab, even if the exchange fails or its Set-Cookie is discarded.
    let hasPriorAuth = true;
    try {
      hasPriorAuth = Boolean(localStorage.getItem('deploy_console_token'))
        || Object.keys(sessionStorage).some((key) => key.startsWith('oidc.'));
      sessionStorage.removeItem(browserSessionKey);
      sessionStorage.setItem(browserModeKey, 'scoped');
    } catch {
      // Storage must work for the tab-local session fence. Otherwise deny.
    }
    return {
      token: window.__mcToolBrowserToken ?? new URLSearchParams(location.hash.slice(1)).get('token') ?? '',
      hasPriorAuth
    };
  });
  const [status, setStatus] = useState(hasPriorAuth
    ? 'Open this tool-token link in a fresh browser context; this tab already has sign-in state.'
    : token ? 'Opening browser session…' : 'Login link is missing a token.');

  // Remove the token from the visible URL before network requests or effects.
  useLayoutEffect(() => {
    window.history.replaceState(null, '', '/login/tool-token');
    delete window.__mcToolBrowserToken;
    if (!hasPriorAuth) window.localStorage.removeItem('deploy_console_token');
  }, [hasPriorAuth]);

  useEffect(() => {
    if (!token || hasPriorAuth) {
      return;
    }
    let canceled = false;
    // Effect replay must cancel dispatch before an exchange can replace cookies.
    queueMicrotask(() => {
      if (canceled) return;
      fetch('/api/browser/session', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      }).then((response) => {
        if (!response.ok) throw new Error('Tool token was rejected for browser access.');
        const sessionId = response.headers.get('X-Browser-Session-Id');
        if (!sessionId) throw new Error('Browser exchange did not return a session identity.');
        if (!canceled) {
          sessionStorage.setItem(browserSessionKey, sessionId);
          navigate('/tool-browser', { replace: true });
        }
      }).catch((error) => {
        if (!canceled) {
          sessionStorage.removeItem(browserSessionKey);
          setStatus(error instanceof Error ? error.message : 'Browser login failed.');
        }
      });
    });
    return () => { canceled = true; };
  }, [hasPriorAuth, navigate, token]);

  return <main className="content"><div className="card"><h1>Browser login</h1><p role="status">{status}</p></div></main>;
}

function ToolBrowserWorkspace() {
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [apps, setApps] = useState<BrowserApp[]>([]);
  const [selected, setSelected] = useState<BrowserApp | null>(null);
  const [conversations, setConversations] = useState<BrowserConversation[]>([]);
  const [conversation, setConversation] = useState<BrowserConversation | null>(null);
  const [messages, setMessages] = useState<BrowserMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('Loading authorized apps…');
  const requestGeneration = useRef(0);
  const closed = useRef(false);
  const csrfToken = useRef<string | null>(null);
  const selectedAppId = useRef<string | null>(null);
  const selectedConversationId = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (closed.current) return;
    const generation = ++requestGeneration.current;
    const appId = selectedAppId.current;
    const conversationId = selectedConversationId.current;
    setSession(null);
    setApps([]);
    setSelected(null);
    setConversations([]);
    setConversation(null);
    setMessages([]);
    setStatus('Loading authorized apps…');
    try {
      const expectedSessionId = sessionStorage.getItem(browserSessionKey);
      if (!expectedSessionId) throw new Error('Open a tool-token login link to start a browser session.');
      const current = await browserGet<BrowserSession>('/api/browser/session');
      if (current.session_id !== expectedSessionId) {
        throw new Error(sessionMismatchText);
      }
      const available = await browserGet<BrowserApp[]>('/api/browser/apps');
      const nextApp = available.find((app) => app.id === appId) ?? null;
      const nextConversations = nextApp && current.capabilities.conversations_read
        ? await browserGet<BrowserConversation[]>(`/api/browser/apps/${encodeURIComponent(nextApp.id)}/conversations`)
        : [];
      const nextConversation = nextConversations.find((item) => item.id === conversationId) ?? null;
      const nextMessages = nextConversation
        ? await browserGet<BrowserMessage[]>(`/api/browser/conversations/${encodeURIComponent(nextConversation.id)}/messages`)
        : [];
      if (requestGeneration.current !== generation) return;
      csrfToken.current = current.csrf_token;
      selectedAppId.current = nextApp?.id ?? null;
      selectedConversationId.current = nextConversation?.id ?? null;
      setSession(current);
      setApps(available);
      setSelected(nextApp);
      setConversations(nextConversations);
      setConversation(nextConversation);
      setMessages(nextMessages);
      setStatus(available.length ? `${available.length} authorized app${available.length === 1 ? '' : 's'}.` : 'No apps are authorized for this token.');
    } catch (error) {
      if (requestGeneration.current !== generation) return;
      selectedAppId.current = null;
      selectedConversationId.current = null;
      const failedSessionId = sessionStorage.getItem(browserSessionKey);
      if (error instanceof Error && (error.message === 'Browser session expired or revoked.' || error.message === sessionMismatchText)) {
        sessionStorage.removeItem(browserSessionKey);
      }
      if (error instanceof Error && error.message === 'Browser session expired or revoked.' && csrfToken.current && failedSessionId) {
        await fetch('/api/browser/session', {
          method: 'DELETE', credentials: 'same-origin', cache: 'no-store',
          headers: { 'X-Browser-CSRF': csrfToken.current, 'X-Browser-Session-Id': failedSessionId }
        }).catch(() => undefined);
        csrfToken.current = null;
      }
      setStatus(error instanceof Error ? error.message : 'Browser session unavailable.');
    }
  }, []);

  useEffect(() => {
    let canceled = false;
    queueMicrotask(() => { if (!canceled) void refresh(); });
    const interval = window.setInterval(() => { void refresh(); }, 60_000);
    return () => { canceled = true; window.clearInterval(interval); };
  }, [refresh]);

  async function inspect(id: string) {
    const generation = ++requestGeneration.current;
    selectedAppId.current = id;
    selectedConversationId.current = null;
    setSelected(null);
    setConversations([]);
    setConversation(null);
    setMessages([]);
    setNewTitle('');
    try {
      const app = await browserGet<BrowserApp>(`/api/browser/apps/${encodeURIComponent(id)}`);
      const available = session?.capabilities.conversations_read
        ? await browserGet<BrowserConversation[]>(`/api/browser/apps/${encodeURIComponent(id)}/conversations`)
        : [];
      if (requestGeneration.current === generation) {
        setSelected(app);
        setConversations(available);
      }
    } catch (error) {
      if (requestGeneration.current !== generation) return;
      setStatus(error instanceof Error ? error.message : 'App unavailable.');
      await refresh();
    }
  }

  async function inspectConversation(id: string) {
    const generation = ++requestGeneration.current;
    selectedConversationId.current = id;
    setConversation(null);
    setMessages([]);
    try {
      const [detail, recent] = await Promise.all([
        browserGet<BrowserConversation>(`/api/browser/conversations/${encodeURIComponent(id)}`),
        browserGet<BrowserMessage[]>(`/api/browser/conversations/${encodeURIComponent(id)}/messages`)
      ]);
      if (requestGeneration.current === generation) {
        setConversation(detail);
        setMessages(recent);
      }
    } catch (error) {
      if (requestGeneration.current !== generation) return;
      setStatus(error instanceof Error ? error.message : 'Conversation unavailable.');
      await refresh();
    }
  }

  async function sendMessage(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !conversation || !draft.trim() || sending) return;
    const generation = requestGeneration.current;
    setSending(true);
    try {
      await browserSend(`/api/browser/conversations/${encodeURIComponent(conversation.id)}/messages`, session.csrf_token, draft);
      if (requestGeneration.current !== generation || closed.current) return;
      setDraft('');
      const recent = await browserGet<BrowserMessage[]>(`/api/browser/conversations/${encodeURIComponent(conversation.id)}/messages`);
      if (requestGeneration.current !== generation || closed.current) return;
      setMessages(recent);
      setStatus('Message queued.');
    } catch (error) {
      if (requestGeneration.current !== generation || closed.current) return;
      setStatus(error instanceof Error ? error.message : 'Message could not be sent.');
      if (error instanceof Error && error.message === 'Browser session expired or revoked.') await refresh();
    } finally {
      setSending(false);
    }
  }

  async function createConversation(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !selected?.can_create_conversation || !newTitle.trim() || creating) return;
    const generation = requestGeneration.current;
    setCreating(true);
    try {
      const id = await browserCreateConversation(selected.id, session.csrf_token, newTitle.trim());
      if (requestGeneration.current !== generation || closed.current) return;
      setNewTitle('');
      const detail = await browserGet<BrowserConversation>(`/api/browser/conversations/${encodeURIComponent(id)}`);
      if (requestGeneration.current !== generation || closed.current) return;
      setConversations((previous) => [detail, ...previous]);
      selectedConversationId.current = id;
      setConversation(detail);
      setMessages([]);
      setStatus('Conversation created.');
    } catch (error) {
      if (requestGeneration.current !== generation || closed.current) return;
      setStatus(error instanceof Error ? error.message : 'Conversation could not be created.');
      if (error instanceof Error && error.message === 'Browser session expired or revoked.') await refresh();
    } finally {
      setCreating(false);
    }
  }

  async function logout() {
    const csrf = session?.csrf_token ?? csrfToken.current;
    const sessionId = sessionStorage.getItem(browserSessionKey);
    closed.current = true;
    sessionStorage.removeItem(browserSessionKey);
    csrfToken.current = null;
    selectedAppId.current = null;
    selectedConversationId.current = null;
    requestGeneration.current += 1;
    setSession(null);
    setApps([]);
    setSelected(null);
    setConversations([]);
    setConversation(null);
    setMessages([]);
    setDraft('');
    setNewTitle('');
    setStatus('Browser session closed.');
    if (csrf && sessionId) {
      await fetch('/api/browser/session', {
        method: 'DELETE', credentials: 'same-origin', cache: 'no-store',
        headers: { 'X-Browser-CSRF': csrf, 'X-Browser-Session-Id': sessionId }
      }).catch(() => undefined);
    }
  }

  return (
    <main className="content">
      <div className="card">
        <h1>Tool browser</h1>
        {session && <a href="/apps">Open Mission Control workspace</a>}
        <p>Access follows this tool token’s current app, server, and operation permissions.</p>
        <p>Open an authorized app to read conversations, create a private chat, or send messages when permitted.</p>
        <p role="status">{status}</p>
        {session && <p>Signed in as {session.subject.name ?? session.subject.email ?? session.subject.id}</p>}
        {session && <button type="button" onClick={() => void refresh()}>Refresh access</button>}
        {session && <button type="button" onClick={() => void logout()}>Logout</button>}
      </div>
      {session && <ToolBrowserInfrastructure key={session.session_id} servers={Boolean(session.capabilities.servers_read)} executors={Boolean(session.capabilities.executors_read)} onExpired={refresh} />}
      {session?.capabilities.grant_management && <ToolBrowserGrants key={session.session_id} csrf={session.csrf_token} onExpired={refresh} />}
      {session?.capabilities.apps_read && <div className="card">
        <h2>Authorized apps</h2>
        <ul>{apps.map((app) => <li key={app.id}>
          <button type="button" onClick={() => void inspect(app.id)}>{app.name}</button>
        </li>)}</ul>
      </div>}
      {selected && <div className="card">
        <h2>{selected.name}</h2><p>Slug: {selected.slug}</p>
        {selected.can_create_conversation && <form onSubmit={(event) => void createConversation(event)}>
          <label htmlFor="tool-browser-conversation-title">New conversation title</label>
          <input id="tool-browser-conversation-title" value={newTitle} maxLength={200}
            onChange={(event) => setNewTitle(event.target.value)} />
          <button type="submit" disabled={creating || !newTitle.trim()}>{creating ? 'Creating…' : 'Create conversation'}</button>
        </form>}
        {session?.capabilities.conversations_read && <>
          <h3>Conversations</h3>
          {conversations.length === 0 && <p>No conversations available for this token.</p>}
          <ul>{conversations.map((item) => <li key={item.id}>
            <button type="button" onClick={() => void inspectConversation(item.id)}>{item.title || item.id}</button>
            <span> {item.status}</span>
          </li>)}</ul>
        </>}
        {!session?.capabilities.conversations_read && <p>Conversation content requires a token with context read access to all servers.</p>}
      </div>}
      {conversation && <div className="card">
        <h2>{conversation.title || 'Conversation'}</h2>
        <p role="status">{conversation.status}</p>
        <ol>{messages.map((message) => <li key={message.id}>
          <strong>{message.role}</strong> <span>{message.status}</span>
          <p style={{ whiteSpace: 'pre-wrap' }}>{message.content}</p>
        </li>)}</ol>
        {conversation.can_send && <form onSubmit={(event) => void sendMessage(event)}>
          <label htmlFor="tool-browser-message">Message</label>
          <textarea id="tool-browser-message" value={draft} maxLength={65536}
            onChange={(event) => setDraft(event.target.value)} />
          <button type="submit" disabled={sending || !draft.trim()}>{sending ? 'Sending…' : 'Send and run'}</button>
        </form>}
      </div>}
      {conversation && session && <ToolBrowserActivity key={`${session.session_id}:${conversation.id}`} conversationId={conversation.id}
        runs={Boolean(session.capabilities.runs_read)} logs={Boolean(session.capabilities.logs_read)} onExpired={refresh} />}
    </main>
  );
}

export default function ToolBrowserPage() {
  const location = useLocation();
  return location.pathname === '/login/tool-token' ? <ToolBrowserLogin /> : <ToolBrowserWorkspace />;
}
